import clsx from 'clsx';

import { BasicAlert, BasicClose, BasicInformation } from '../../icons/basic';
import { Typography } from '../typography/Typography';

type NotificationType = 'warning' | 'notification' | 'information';

type Props = {
  title: string;
  description?: string;
  type: NotificationType;
  wrapperClassname?: string;
  id?: string | number;
  handleClickClose?: (id: string | number) => void;
  closeText?: string;
  className?: string;
}

const NOTIFICATION_CONFIG: Record<NotificationType, {
  icon: typeof BasicAlert;
  bg: string;
  iconColor: string;
  titleColor: string;
  textColor: string;
  closeIconColor: string;
}> = {
  warning: {
    icon: BasicAlert,
    bg: 'bg-notification-warning',
    iconColor: 'text-notification-warning-icon',
    titleColor: 'text-notification-warning-title',
    textColor: 'text-notification-warning-text',
    closeIconColor: 'text-notification-warning-icon-close',
  },
  notification: {
    icon: BasicInformation,
    bg: 'bg-notification-notification',
    iconColor: 'text-notification-notification-icon',
    titleColor: 'text-notification-notification-title',
    textColor: 'text-notification-notification-text',
    closeIconColor: 'text-notification-notification-icon-close',
  },
  information: {
    icon: BasicInformation,
    bg: 'bg-notification-information',
    iconColor: 'text-notification-information-icon',
    titleColor: 'text-notification-information-title',
    textColor: 'text-notification-information-text',
    closeIconColor: 'text-notification-information-icon-close',
  },
};

export const Notification = ({
  title,
  description,
  type,
  wrapperClassname,
  id,
  handleClickClose,
  closeText,
  className,
}: Props) => {
  const config = NOTIFICATION_CONFIG[type];
  const Icon = config.icon;

  return (
    <section
      aria-label={title}
      className={clsx('flex justify-between px-6 py-4', config.bg, wrapperClassname, className)}
    >
      <div className="flex items-start gap-2">
        <Icon className={clsx('size-6 shrink-0', config.iconColor)} />
        <div className="flex min-w-0 flex-1 flex-wrap items-start gap-x-6 gap-y-2">
          <Typography
            variant="label-regular"
            className={clsx('max-w-full', config.titleColor)}
            type="p"
          >
            {title}
          </Typography>
          {description ? (
            <Typography
              variant="content-body"
              className={clsx('prose max-w-full prose-a:text-inherit', config.textColor)}
              type="div"
              dangerouslySetInnerHTML={description}
            />
          ) : null}
        </div>
      </div>

      {handleClickClose ? (
        <button
          className="-m-2 size-10 shrink-0 p-2"
          onClick={id != null ? () => handleClickClose(id) : undefined}
        >
          <BasicClose className={config.closeIconColor} />
          <span className="sr-only">{closeText}</span>
        </button>
      ) : null}
    </section>
  );
};
